import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/analytics"

export default class extends Controller {
  static targets = ["input", "dropzone", "idle", "loading", "success", "error", "errorMessage"]
  static values = {
    url: String,
    requestId: Number
  }

  connect() {
    this.maxFileSize = 10 * 1024 * 1024 // 10 MB
  }

  triggerFileInput(event) {
    // Le clic programmatique sur l'input caché remonte (bubbling) jusqu'à la
    // dropzone qui porte aussi `click->triggerFileInput`. Sans ce garde, on
    // ré-entre dans cette méthode et le navigateur annule l'ouverture du
    // sélecteur de fichier (il ne s'ouvre jamais). On ignore donc le clic
    // qui provient de l'input lui-même.
    if (event.target === this.inputTarget) return
    event.preventDefault()
    this.inputTarget.click()
  }

  dragover(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  dragenter(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("border-terracotta", "bg-terracotta/10")
      this.dropzoneTarget.classList.remove("border-beige")
    }
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-terracotta", "bg-terracotta/10")
      this.dropzoneTarget.classList.add("border-beige")
    }
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-terracotta", "bg-terracotta/10")
      this.dropzoneTarget.classList.add("border-beige")
    }

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.uploadFiles(files)
    }
  }

  upload(event) {
    const files = event.target.files
    if (files.length > 0) {
      this.uploadFiles(files)
    }
  }

  async uploadFiles(files) {
    // Validate files
    for (const file of files) {
      if (file.size > this.maxFileSize) {
        this.showError(`Le fichier "${file.name}" dépasse la taille maximale de 10 Mo.`)
        return
      }
    }

    this.showLoading()

    const formData = new FormData()
    formData.append("document_request_id", this.requestIdValue)

    for (const file of files) {
      formData.append("files[]", file)
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        track("document-depose", { nombre: files.length })
        this.showSuccess()
        // Reload the page after a short delay to show updated state
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        this.showError(data.error || "Une erreur est survenue lors de l'envoi.")
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.showError("Une erreur est survenue lors de l'envoi. Veuillez réessayer.")
    }

    // Reset file input
    this.inputTarget.value = ""
  }

  showLoading() {
    if (this.hasIdleTarget) this.idleTarget.classList.add("hidden")
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
    if (this.hasSuccessTarget) this.successTarget.classList.add("hidden")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }

  showSuccess() {
    if (this.hasIdleTarget) this.idleTarget.classList.add("hidden")
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    if (this.hasSuccessTarget) this.successTarget.classList.remove("hidden")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }

  showError(message) {
    if (this.hasIdleTarget) this.idleTarget.classList.add("hidden")
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    if (this.hasSuccessTarget) this.successTarget.classList.add("hidden")
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("hidden")
      if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.textContent = message
      }
    }

    // Reset to idle state after a delay
    setTimeout(() => {
      this.showIdle()
    }, 5000)
  }

  showIdle() {
    if (this.hasIdleTarget) this.idleTarget.classList.remove("hidden")
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    if (this.hasSuccessTarget) this.successTarget.classList.add("hidden")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }
}
